import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Star, CheckCircle, Warning, Lock } from '@phosphor-icons/react';
import { ratingAPI } from '../lib/api';
import { toast } from 'sonner';

const SATISFACTION_OPTIONS = [
  { value: 'muy_satisfecho', label: 'Muy satisfactorio', color: 'text-green-600' },
  { value: 'satisfecho', label: 'Satisfactorio', color: 'text-emerald-600' },
  { value: 'poco_satisfecho', label: 'Poco satisfactorio', color: 'text-amber-600' },
  { value: 'nada_satisfecho', label: 'Nada satisfactorio', color: 'text-red-600' },
];

/**
 * Rating dialog used by both roles at the end of the transaction flow.
 * Backend gates: can-rate returns can_rate=true only when chat_room.is_active.
 */
const RatingDialog = ({ open, onOpenChange, quotationId, toUserId, toUserName, role, onSubmitted }) => {
  const [stars, setStars] = useState(5);
  const [hoverStars, setHoverStars] = useState(0);
  const [satisfaction, setSatisfaction] = useState('satisfecho');
  const [comment, setComment] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setStars(5);
      setHoverStars(0);
      setSatisfaction('satisfecho');
      setComment('');
      setAcknowledged(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!toUserId) {
      toast.error('Falta información del usuario a calificar');
      return;
    }
    setSubmitting(true);
    try {
      await ratingAPI.create({
        quotation_id: quotationId,
        to_user_id: toUserId,
        stars,
        satisfaction,
        comment: comment.trim() || null,
      });
      toast.success('¡Calificación enviada!');
      onOpenChange(false);
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar la calificación');
    } finally {
      setSubmitting(false);
    }
  };

  const targetLabel = role === 'cliente' ? 'Proveedor' : 'Cliente';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-none max-h-[90vh] flex flex-col p-0 gap-0" data-testid="rating-dialog">
        <DialogHeader className="p-6 pb-3 border-b border-zinc-200 dark:border-zinc-800 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-400" weight="fill" />
            Califica al {targetLabel}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 px-6 py-4 overflow-y-auto flex-1 min-h-0">
          {/* CRITICAL WARNING — chat will close and data will be hidden */}
          <div className="border-2 border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4" data-testid="rating-close-warning">
            <div className="flex items-start gap-3">
              <Warning weight="fill" className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="font-bold text-red-800 dark:text-red-300 text-sm">
                  IMPORTANTE — Lee antes de continuar
                </p>
                <p className="text-sm text-red-700 dark:text-red-400 leading-relaxed">
                  Al enviar tu calificación:
                </p>
                <ul className="text-sm text-red-700 dark:text-red-400 space-y-1 list-disc list-inside">
                  <li><strong>El chat se cerrará permanentemente</strong> para ambas partes.</li>
                  <li>Los datos de contacto <strong>del {targetLabel.toLowerCase()} dejarán de ser visibles</strong> en la cotización.</li>
                  <li>Podrás consultar el historial del chat pero <strong>no enviar nuevos mensajes</strong>.</li>
                </ul>
                <p className="text-xs text-red-700 dark:text-red-400 italic pt-1">
                  Esto protege tu operación y asegura que todas las nuevas transacciones pasen por la plataforma.
                  Confirma que ya completaste todos los acuerdos antes de calificar.
                </p>
              </div>
            </div>
          </div>

          {toUserName && (
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Calificando a <strong className="text-zinc-900 dark:text-zinc-100">{toUserName}</strong>. Esta calificación es permanente.
            </p>
          )}

          {/* Stars */}
          <div>
            <Label className="text-xs text-zinc-500 mb-2 block">Estrellas</Label>
            <div className="flex gap-1" data-testid="stars-picker">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onMouseEnter={() => setHoverStars(n)}
                  onMouseLeave={() => setHoverStars(0)}
                  onClick={() => setStars(n)}
                  className="p-1 focus:outline-none"
                  data-testid={`star-${n}`}
                >
                  <Star
                    className={`w-8 h-8 transition-transform ${
                      n <= (hoverStars || stars) ? 'text-amber-400 scale-110' : 'text-zinc-300 dark:text-zinc-600'
                    }`}
                    weight={n <= (hoverStars || stars) ? 'fill' : 'regular'}
                  />
                </button>
              ))}
              <span className="ml-3 self-center text-sm font-medium text-zinc-700 dark:text-zinc-300">
                {stars} / 5
              </span>
            </div>
          </div>

          {/* Satisfaction */}
          <div>
            <Label className="text-xs text-zinc-500 mb-2 block">Nivel de satisfacción</Label>
            <RadioGroup value={satisfaction} onValueChange={setSatisfaction} className="space-y-1.5">
              {SATISFACTION_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-3 border border-zinc-200 dark:border-zinc-700 p-2.5 cursor-pointer hover:border-orange-400 transition-colors"
                  data-testid={`satisfaction-${opt.value}`}
                >
                  <RadioGroupItem value={opt.value} />
                  <span className={`text-sm ${satisfaction === opt.value ? opt.color + ' font-medium' : 'text-zinc-700 dark:text-zinc-300'}`}>
                    {opt.label}
                  </span>
                </label>
              ))}
            </RadioGroup>
          </div>

          {/* Comment */}
          <div>
            <Label htmlFor="rating-comment" className="text-xs text-zinc-500 mb-2 block">
              Comentario <span className="text-zinc-400">(opcional — solo visible para quien lo recibe)</span>
            </Label>
            <Textarea
              id="rating-comment"
              rows={3}
              maxLength={500}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Escribe un comentario sobre tu experiencia..."
              className="rounded-none"
              data-testid="rating-comment"
            />
            <p className="text-xs text-zinc-400 text-right mt-1">{comment.length}/500</p>
          </div>

          {/* Acknowledgement checkbox */}
          <label className="flex items-start gap-2 cursor-pointer select-none p-3 border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 hover:border-orange-400 transition-colors" data-testid="rating-ack-label">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1 accent-orange-600"
              data-testid="rating-ack-checkbox"
            />
            <span className="text-sm text-zinc-800 dark:text-zinc-200">
              <Lock className="inline w-4 h-4 mr-1 text-zinc-500" />
              Entiendo que al enviar esta calificación, el chat se cerrará y los datos de contacto del {targetLabel.toLowerCase()} dejarán de ser visibles.
            </span>
          </label>
        </div>

        <DialogFooter className="p-6 pt-4 border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shrink-0 sm:flex-row flex-col gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="rounded-none w-full sm:w-auto"
            data-testid="rating-cancel"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !acknowledged}
            className="btn-primary rounded-none disabled:opacity-50 w-full sm:w-auto"
            data-testid="rating-submit"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            {submitting ? 'Enviando...' : 'Enviar y cerrar chat'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RatingDialog;
